'use client'

// Reveal-on-scroll wrapper for the marketing pages.
//
// The transition is the shared spring (`--rt-ease-spring`), not a hand-picked
// cubic-bezier: design-language.md §3 asks for springs specifically because a
// fixed curve reads as choreographed. See packages/design/src/motion.ts for how
// the app's actual spring gets from Reanimated to CSS.
//
// `index` rather than a raw `delay`: the stagger between two elements that
// change together is a design constant (70ms), not a per-call-site number, and
// passing the constant around as milliseconds is how it drifted to 52ms in the
// editor and 90ms on the landing page.

import { useEffect, useRef, useState } from 'react'
import { STAGGER_MAX_STEPS, STAGGER_MS } from '@roomtreatment/design'

interface RevealProps {
  children: React.ReactNode
  className?: string
  /** Position in a staggered group. 0 is immediate; each step adds 70ms. */
  index?: number
}

export default function Reveal({ children, className = '', index = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  // Index 0 (hero elements) starts visible immediately so LCP is painted without waiting for observer callback
  const [shown, setShown] = useState(index === 0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          io.disconnect()
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const delay = Math.min(index, STAGGER_MAX_STEPS) * STAGGER_MS
  const ease = 'var(--rt-ease-spring)'
  const dur = 'var(--rt-spring-ms)'

  return (
    <div
      ref={ref}
      // `rt-reveal`: reduced motion shows it at rest (globals.css).
      className={`rt-reveal ${className}`}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(20px)',
        transition: `opacity ${dur} ${ease} ${delay}ms, transform ${dur} ${ease} ${delay}ms`,
      }}
    >
      {children}
    </div>
  )
}
